"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useEffect, useRef, useState } from "react";
export default function CompanyOverview() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!imageContainerRef.current) return;
      const rect = imageContainerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementHeight = rect.height;
      if (elementTop < windowHeight && elementTop + elementHeight > 0) {
        const visibleHeight = Math.min(windowHeight - elementTop, elementHeight);
        const progress = visibleHeight / elementHeight;
        setScrollProgress(Math.min(Math.max(progress, 0), 1));
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const scale = 1 + scrollProgress * 0.05;
  return <section id="company-overview" className="relative min-h-screen bg-[#F6F3EC] overflow-hidden">
      <div className="container mx-auto px-6 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div ref={textRef} className={`space-y-6 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <div className="inline-block">
              <span className="text-sm font-display font-semibold uppercase tracking-wider text-primary/70">
                Who We Are
              </span>
              <div className="h-0.5 w-12 bg-accent mt-2"></div>
            </div>

            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-primary leading-tight">
              Connecting Markets,
              <br />
              <span className="text-secondary">Delivering Trust</span>
            </h2>

            <p className="text-lg text-foreground/80 leading-relaxed max-w-xl">
              Global Wholesale Trade is a premium international sourcing and
              commodity trading company, positioning itself as a reliable global
              partner for moving goods from origin to market.
            </p>

            <p className="text-base text-muted-foreground leading-relaxed max-w-xl">
              Serving importers, wholesalers, distributors, manufacturers,
              retailers, and HoReCa buyers across the Middle East, Africa, Asia,
              and Europe, we deliver transparent procurement, rigorous quality
              assurance, and coordinated logistics.
            </p>

            <div className="pt-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                <p className="text-foreground/70">
                  <span className="font-semibold text-foreground">
                    Transparency:
                  </span>{" "}
                  Clear communication at every step
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                <p className="text-foreground/70">
                  <span className="font-semibold text-foreground">
                    Reliability:
                  </span>{" "}
                  Consistent quality and on-time delivery
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                <p className="text-foreground/70">
                  <span className="font-semibold text-foreground">Speed:</span>{" "}
                  Efficient processes that respect your timelines
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                <p className="text-foreground/70">
                  <span className="font-semibold text-foreground">
                    Compliance:
                  </span>{" "}
                  International standards and certifications
                </p>
              </div>
            </div>
          </div>

          {}
          <div ref={imageContainerRef} className="relative lg:h-[600px] h-[400px] overflow-hidden rounded-sm shadow-strong">
            <AspectRatio ratio={4 / 3} className="h-full">
              <div className="w-full h-full transition-transform duration-[var(--duration-slow)] ease-[var(--ease-smooth)]" style={{
              transform: `scale(${scale})`,
              transformOrigin: "center center"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542426/img/deep_water_port_logistics_hub.png" alt="Deep-water port with modern logistics operations" className="w-full h-full object-cover" />
              </div>
            </AspectRatio>

            {}
            <div className="absolute inset-0 bg-primary/10 pointer-events-none"></div>
          </div>
        </div>
      </div>
    </section>;
}